import * as React from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Link,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getUserTags, previewPollAudience, sendPoll } from '../../Api';
import type {
    PollAudienceDto,
    PollDto,
    PollSendDto,
    PollSendStatus,
    PollStatus,
    PollSummaryDto,
} from '../../models/dtos/PollDto';
import { POLL_LANGUAGE_LABELS } from '../../models/dtos/PollDto';
import type { UserTagCombinationMode } from '../../models/UserTagCombinationMode';

export const formatDateTime = (value?: string | null) => (value ? new Date(value).toLocaleString() : '—');

export const percent = (part: number, total: number) => (total > 0 ? `${((part / total) * 100).toFixed(1)}%` : '—');

export const languageLabel = (code?: string | null) =>
    code ? `${code.toUpperCase()}${POLL_LANGUAGE_LABELS[code] ? ` · ${POLL_LANGUAGE_LABELS[code]}` : ''}` : 'Unknown';

const POLL_STATUS_COLORS: Record<PollStatus, 'default' | 'success' | 'warning'> = {
    DRAFT: 'default',
    ACTIVE: 'success',
    CLOSED: 'warning',
};

export function PollStatusChip({ status }: { status: PollStatus }) {
    return <Chip size="small" label={status.toLowerCase()} color={POLL_STATUS_COLORS[status]} />;
}

const SEND_STATUS_COLORS: Record<PollSendStatus, 'default' | 'info' | 'success' | 'error'> = {
    PENDING: 'default',
    SENDING: 'info',
    COMPLETED: 'success',
    FAILED: 'error',
};

export function PollSendStatusChip({ send }: { send: PollSendDto }) {
    const chip = <Chip size="small" label={send.status.toLowerCase()} color={SEND_STATUS_COLORS[send.status]} />;
    return send.error ? <Tooltip title={send.error}>{chip}</Tooltip> : chip;
}

/** targeted · answered · dismissed · response rate, one line. */
export function PollSummaryLine({ summary }: { summary?: PollSummaryDto | null }) {
    const s = summary ?? { targeted: 0, opened: 0, answered: 0, dismissed: 0 };
    return (
        <Typography variant="body2" color="text.secondary">
            {s.targeted.toLocaleString()} targeted · {s.opened.toLocaleString()} opened · {s.answered.toLocaleString()} answered
            · {s.dismissed.toLocaleString()} closed · {percent(s.answered, s.targeted)} response rate
        </Typography>
    );
}

/** Sends table used on the polls page (all polls) and on the stats page (one poll). */
export function PollSendsTable({ sends, showPoll, selectedSendId, onSelect }: {
    sends: PollSendDto[];
    showPoll?: boolean;
    selectedSendId?: string | null;
    onSelect?: (send: PollSendDto) => void;
}) {
    const navigate = useNavigate();
    if (sends.length === 0) {
        return <Typography variant="body2" color="text.secondary">Nothing sent yet.</Typography>;
    }
    return (
        <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Sent</TableCell>
                        {showPoll && <TableCell>Poll</TableCell>}
                        <TableCell>Audience</TableCell>
                        <TableCell align="right">Targeted</TableCell>
                        <TableCell align="right">Queued</TableCell>
                        <TableCell align="right">Skipped</TableCell>
                        <TableCell align="right">Answered</TableCell>
                        <TableCell align="right">Closed</TableCell>
                        <TableCell align="right">Response</TableCell>
                        <TableCell>Status</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sends.map(send => {
                        const summary = send.summary;
                        return (
                            <TableRow
                                key={send.id}
                                hover={!!onSelect}
                                selected={selectedSendId === send.id}
                                onClick={onSelect ? () => onSelect(send) : undefined}
                                sx={{ cursor: onSelect ? 'pointer' : undefined }}
                            >
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(send.createdAt)}</TableCell>
                                {showPoll && (
                                    <TableCell>
                                        <Link component="button" variant="body2"
                                            onClick={() => navigate('/marketing/polls/' + encodeURIComponent(send.pollId) + '/stats')}>
                                            {send.pollName ?? send.pollId}
                                        </Link>
                                    </TableCell>
                                )}
                                <TableCell>
                                    {send.test ? (
                                        <Chip size="small" variant="outlined" label={`test · ${send.testUserIds.join(', ')}`} />
                                    ) : (
                                        <Stack direction="row" gap={0.5} flexWrap="wrap" alignItems="center">
                                            {send.userTags.map(tag => <Chip key={tag} size="small" label={tag} />)}
                                            {send.userTags.length > 1 && (
                                                <Typography variant="caption" color="text.secondary">{send.tagCombinationMode}</Typography>
                                            )}
                                            {send.activeWithinDays != null && (
                                                <Typography variant="caption" color="text.secondary">active ≤ {send.activeWithinDays}d</Typography>
                                            )}
                                            {send.maxUsers != null && (
                                                <Typography variant="caption" color="text.secondary">max {send.maxUsers.toLocaleString()}</Typography>
                                            )}
                                        </Stack>
                                    )}
                                </TableCell>
                                <TableCell align="right">{send.targetedCount.toLocaleString()}</TableCell>
                                <TableCell align="right">
                                    {send.pushedCount.toLocaleString()}
                                    {send.failedCount > 0 && (
                                        <Typography component="span" variant="caption" color="error.main"> ({send.failedCount} not queued)</Typography>
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    <Tooltip title={`${send.skippedAnsweredCount} already answered, ${send.skippedDismissedCount} closed before`}>
                                        <span>{(send.skippedAnsweredCount + send.skippedDismissedCount).toLocaleString()}</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell align="right">{(summary?.answered ?? 0).toLocaleString()}</TableCell>
                                <TableCell align="right">{(summary?.dismissed ?? 0).toLocaleString()}</TableCell>
                                <TableCell align="right">{percent(summary?.answered ?? 0, summary?.targeted ?? 0)}</TableCell>
                                <TableCell><PollSendStatusChip send={send} /></TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </Box>
    );
}

/**
 * Send to users with the picked user tags: preview counts the audience per language (and which poll
 * language each group gets), then a confirmation starts the send in the background.
 */
export function PollSendDialog({ poll, open, onClose, onSent }: {
    poll: PollDto | null;
    open: boolean;
    onClose: () => void;
    onSent: (send: PollSendDto) => void;
}) {
    const [tagOptions, setTagOptions] = React.useState<string[]>([]);
    const [userTags, setUserTags] = React.useState<string[]>([]);
    const [mode, setMode] = React.useState<UserTagCombinationMode>('ANY');
    const [activeWithinDays, setActiveWithinDays] = React.useState('30');
    const [maxUsers, setMaxUsers] = React.useState('');
    const [skipDismissed, setSkipDismissed] = React.useState(false);
    const [audience, setAudience] = React.useState<PollAudienceDto | null>(null);
    const [busy, setBusy] = React.useState(false);
    const [confirming, setConfirming] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!open) return;
        setAudience(null);
        setConfirming(false);
        setError(null);
        getUserTags().then(tags => setTagOptions((tags ?? []).map(t => t.tagName).sort()));
    }, [open]);

    // Any change of the audience invalidates the preview.
    React.useEffect(() => {
        setAudience(null);
        setConfirming(false);
    }, [userTags, mode, activeWithinDays]);

    const parsedDays = activeWithinDays.trim() ? Number(activeWithinDays) : null;
    const parsedMax = maxUsers.trim() ? Number(maxUsers) : null;
    const invalid = userTags.length === 0
        || (parsedDays != null && (!Number.isInteger(parsedDays) || parsedDays < 1))
        || (parsedMax != null && (!Number.isInteger(parsedMax) || parsedMax < 1));

    const audienceRequest = () => ({ userTags, tagCombinationMode: mode, activeWithinDays: parsedDays });

    const preview = async () => {
        if (!poll) return;
        setBusy(true);
        setError(null);
        const result = await previewPollAudience(audienceRequest(), poll.id);
        setBusy(false);
        if (!result.ok) {
            setError(result.error);
            return;
        }
        setAudience(result.data);
    };

    const send = async () => {
        if (!poll) return;
        setBusy(true);
        setError(null);
        const result = await sendPoll(poll.id, { ...audienceRequest(), skipDismissed, maxUsers: parsedMax });
        setBusy(false);
        if (!result.ok) {
            setError(result.error);
            return;
        }
        onSent(result.data);
    };

    const fallbackUsers = audience?.languages
        .filter(l => l.servedLanguage != null && l.servedLanguage !== l.lang)
        .reduce((sum, l) => sum + l.count, 0) ?? 0;
    const expected = audience
        ? Math.min(Math.max(0, audience.totalUsers - (audience.alreadyAnswered ?? 0)), parsedMax ?? Number.MAX_SAFE_INTEGER)
        : 0;

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Send “{poll?.name}”</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{ pt: 1 }}>
                    <Autocomplete
                        multiple
                        options={tagOptions}
                        value={userTags}
                        onChange={(_, value) => setUserTags(value)}
                        renderInput={params => <TextField {...params} label="User tags" required helperText="Only users with these tags get the push" />}
                    />
                    {userTags.length > 1 && (
                        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, value) => value && setMode(value)}>
                            <ToggleButton value="ANY">Any of the tags</ToggleButton>
                            <ToggleButton value="ALL">All of the tags</ToggleButton>
                        </ToggleButtonGroup>
                    )}
                    <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                        <TextField
                            label="Active within (days)"
                            value={activeWithinDays}
                            onChange={e => setActiveWithinDays(e.target.value)}
                            helperText="Empty = every tagged user"
                            fullWidth
                        />
                        <TextField
                            label="Max users"
                            value={maxUsers}
                            onChange={e => setMaxUsers(e.target.value)}
                            helperText="Empty = no cap"
                            fullWidth
                        />
                    </Stack>
                    <FormControlLabel
                        control={<Checkbox checked={skipDismissed} onChange={e => setSkipDismissed(e.target.checked)} />}
                        label="Skip users who closed this poll before"
                    />
                    <Typography variant="caption" color="text.secondary">
                        Users who already answered are always skipped.
                    </Typography>

                    {audience && (
                        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                            <Typography variant="subtitle2">
                                {audience.totalUsers.toLocaleString()} users match
                                {audience.alreadyAnswered ? ` · ${audience.alreadyAnswered.toLocaleString()} already answered` : ''}
                            </Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>User language</TableCell>
                                        <TableCell align="right">Users</TableCell>
                                        <TableCell>Gets poll in</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {audience.languages.map(l => (
                                        <TableRow key={l.lang ?? 'unknown'}>
                                            <TableCell>{languageLabel(l.lang)}</TableCell>
                                            <TableCell align="right">{l.count.toLocaleString()}</TableCell>
                                            <TableCell sx={{ color: l.servedLanguage !== l.lang ? 'warning.main' : undefined }}>
                                                {l.servedLanguage?.toUpperCase() ?? '—'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {fallbackUsers > 0 && (
                                <Typography variant="caption" color="warning.main">
                                    {fallbackUsers.toLocaleString()} users get the poll in the fallback language — add translations to reach them in theirs.
                                </Typography>
                            )}
                        </Box>
                    )}
                    {confirming && (
                        <Alert severity="warning">
                            About {expected.toLocaleString()} users will get a push now. This cannot be undone.
                        </Alert>
                    )}
                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </DialogContent>
            <DialogActions>
                {busy && <CircularProgress size={20} sx={{ mr: 1 }} />}
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                {!audience ? (
                    <Button variant="outlined" onClick={preview} disabled={busy || invalid}>Count audience</Button>
                ) : !confirming ? (
                    <Button variant="contained" onClick={() => setConfirming(true)} disabled={busy || invalid || expected === 0}>
                        Send
                    </Button>
                ) : (
                    <Button variant="contained" color="warning" onClick={send} disabled={busy || invalid}>
                        Confirm send
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
